import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MembershipTierCard } from '../components/MembershipTierCard';
import { PackOfferingCard } from '../components/PackOfferingCard';
import { PayPackPanel } from '../components/PayPackPanel';
import {
  type CreditPack,
  CustomerApiError,
  type CustomerMembership,
  type MembershipTier,
  type PackOffering,
  type PacksListResponse,
  packsApi,
} from '../customer_api';
import { formatDate } from '../lib/format';
import { getStripePublishableKey } from '../lib/stripe_env';

// ---------------------------------------------------------------------------
// Small inline primitives
// ---------------------------------------------------------------------------

function SectionTitle({ children }: { children: string }) {
  return (
    <h2
      style={{
        fontSize: '1.1rem',
        fontWeight: 600,
        margin: '0 0 0.75rem',
        letterSpacing: '-0.01em',
      }}
    >
      {children}
    </h2>
  );
}

function EmptyNote({ children }: { children: string }) {
  return (
    <p
      style={{
        margin: 0,
        fontSize: '0.875rem',
        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
      }}
    >
      {children}
    </p>
  );
}

function Badge({ variant, children }: { variant: 'warning' | 'muted'; children: string }) {
  const styles =
    variant === 'warning'
      ? {
          background: 'oklch(0.94 0.06 85)',
          color: 'oklch(0.50 0.10 65)',
          border: '1px solid oklch(0.85 0.08 85)',
        }
      : {
          background: 'oklch(0.94 0.005 80)',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          border: '1px solid var(--border, oklch(0.915 0.005 80))',
        };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.15rem 0.6rem',
        borderRadius: '999px',
        fontSize: '0.72rem',
        fontWeight: 500,
        ...styles,
      }}
    >
      {children}
    </span>
  );
}

function UpgradePrompt({
  currentTier,
  requiredTiers,
}: {
  currentTier: string;
  requiredTiers: string[];
}) {
  return (
    <div
      style={{
        padding: '2rem 1.5rem',
        borderRadius: '14px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        background: 'var(--surface, white)',
        textAlign: 'center',
      }}
    >
      <p
        style={{
          margin: '0 0 0.5rem',
          fontWeight: 600,
          fontSize: '0.95rem',
        }}
      >
        Daycare packs and memberships not available
      </p>
      <p
        style={{
          margin: 0,
          fontSize: '0.875rem',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
        }}
      >
        This feature requires the{' '}
        {requiredTiers.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(' or ')} plan. Your
        kennel is currently on the {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}{' '}
        plan. Contact your kennel to upgrade.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

interface TierGateError {
  feature: string;
  current_tier: string;
  required_tiers: string[];
}

interface PurchaseState {
  offeringId: string;
  amountMinor: number;
  currency: string;
  stripeAccountId: string;
}

export function Packs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PacksListResponse | null>(null);
  const [tierGate, setTierGate] = useState<TierGateError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [purchaseState, setPurchaseState] = useState<PurchaseState | null>(null);
  const [subscribingTierId, setSubscribingTierId] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const subscribedSuccess = searchParams.get('subscribed') === '1';

  const loadPacks = useCallback(() => {
    setError(null);
    setTierGate(null);
    packsApi
      .list()
      .then((d) => setData(d))
      .catch((err) => {
        if (err instanceof CustomerApiError && err.status === 402) {
          const body = err.body as TierGateError;
          setTierGate(body);
        } else {
          setError('Could not load packs. Please try again.');
        }
      });
  }, []);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  // Dismiss the ?subscribed=1 query param after a short read.
  useEffect(() => {
    if (!subscribedSuccess) return;
    const t = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('subscribed');
        return next;
      });
    }, 5000);
    return () => clearTimeout(t);
  }, [subscribedSuccess, setSearchParams]);

  const startPackPurchase = useCallback(
    (offering: PackOffering) => {
      if (!data?.stripe_account_id) {
        setError('Payments not configured for this kennel. Please contact them directly.');
        return;
      }
      const pk = getStripePublishableKey();
      if (!pk) {
        setError('Payments not available at this time. Please try again later.');
        return;
      }
      setPurchaseState({
        offeringId: offering.id,
        amountMinor: offering.priceMinor,
        currency: offering.currency,
        stripeAccountId: data.stripe_account_id,
      });
    },
    [data],
  );

  const handlePaid = useCallback(() => {
    setPurchaseState(null);
    // Reload to show the new pack in the "Active packs" section.
    loadPacks();
  }, [loadPacks]);

  const startSubscribe = useCallback(async (tierId: string) => {
    setSubscribingTierId(tierId);
    try {
      const { url } = await packsApi.subscribeMembership(tierId);
      window.location.href = url;
    } catch {
      setError('Could not start subscription. Please try again.');
      setSubscribingTierId(null);
    }
  }, []);

  const openPortal = useCallback(async () => {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      const { url } = await packsApi.membershipPortalLink();
      window.location.href = url;
    } catch {
      setPortalError('Could not open the billing portal. Please try again.');
      setOpeningPortal(false);
    }
  }, []);

  const activeMemberships = (data?.memberships ?? []).filter(
    (m) => m.status === 'active' || m.status === 'past_due',
  );

  const activeOfferings = (data?.offerings ?? []).filter((o) => o.active === 1);
  const activeTiers = (data?.tiers ?? []).filter((t) => t.active === 1);

  const getTierName = (tierId: string): string => {
    const tier = data?.tiers.find((t) => t.id === tierId);
    return tier?.name ?? tierId;
  };

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <span
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Account
        </span>
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 1.5rem',
          }}
        >
          Packs &amp; memberships
        </h1>

        {/* Success banner after returning from Stripe Checkout */}
        {subscribedSuccess && (
          <div
            role="status"
            style={{
              marginBottom: '1.25rem',
              padding: '0.875rem 1.125rem',
              borderRadius: '10px',
              border: '1px solid oklch(0.55 0.12 150 / 0.30)',
              background: 'oklch(0.55 0.12 150 / 0.08)',
              color: 'oklch(0.36 0.12 150)',
              fontSize: '0.875rem',
            }}
          >
            Subscription started successfully. Your membership will appear here shortly.
          </div>
        )}

        {error && (
          <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))', marginBottom: '1rem' }}>
            {error}
          </p>
        )}

        {/* Tier gate */}
        {tierGate && (
          <UpgradePrompt
            currentTier={tierGate.current_tier}
            requiredTiers={tierGate.required_tiers}
          />
        )}

        {/* Loading */}
        {!tierGate && data === null && !error && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {data !== null && (
          <>
            {/* Active packs */}
            <section style={{ marginBottom: '2rem' }}>
              <SectionTitle>Active packs</SectionTitle>
              {data.active_packs.length === 0 ? (
                <EmptyNote>No active packs.</EmptyNote>
              ) : (
                <div style={{ borderTop: '1px solid var(--border, oklch(0.915 0.005 80))' }}>
                  {data.active_packs.map((pack: CreditPack) => (
                    <div
                      key={pack.id}
                      style={{
                        padding: '0.75rem 0',
                        borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.2rem',
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        <span className="num">{pack.packSize}</span>-visit pack
                      </div>
                      <div
                        style={{
                          fontSize: '0.78rem',
                          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                          display: 'flex',
                          gap: '0.5rem',
                        }}
                      >
                        <span className="num">{pack.visitsRemaining}</span> of{' '}
                        <span className="num">{pack.packSize}</span> visits remaining
                        <span aria-hidden="true">·</span>
                        <span>Expires {formatDate(pack.expiresAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Active memberships */}
            <section style={{ marginBottom: '2rem' }}>
              <SectionTitle>Active memberships</SectionTitle>
              {activeMemberships.length === 0 ? (
                <EmptyNote>No active memberships.</EmptyNote>
              ) : (
                <div style={{ borderTop: '1px solid var(--border, oklch(0.915 0.005 80))' }}>
                  {activeMemberships.map((m: CustomerMembership) => (
                    <div
                      key={m.id}
                      style={{
                        padding: '0.75rem 0',
                        borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.35rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {getTierName(m.tierId)}
                        </span>
                        {m.status === 'past_due' && <Badge variant="warning">Payment failed</Badge>}
                        {m.cancelAtPeriodEnd === 1 && (
                          <Badge variant="muted">Cancelling at period end</Badge>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: '0.78rem',
                          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                          display: 'flex',
                          gap: '0.5rem',
                        }}
                      >
                        <span className="num">{m.visitsRemaining}</span> visits remaining this cycle
                        <span aria-hidden="true">·</span>
                        <span>Renews {formatDate(m.currentPeriodEnd)}</span>
                      </div>
                      <div style={{ marginTop: '0.25rem' }}>
                        <button
                          type="button"
                          onClick={openPortal}
                          disabled={openingPortal}
                          style={{
                            padding: '0.35rem 0.875rem',
                            borderRadius: '7px',
                            border: '1px solid var(--border, oklch(0.915 0.005 80))',
                            background: 'var(--surface, white)',
                            fontSize: '0.8rem',
                            cursor: openingPortal ? 'not-allowed' : 'pointer',
                            color: 'var(--foreground, oklch(0.19 0.012 75))',
                          }}
                        >
                          {openingPortal ? 'Opening…' : 'Manage subscription'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {portalError && (
                <p
                  style={{
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--destructive, oklch(0.55 0.15 30))',
                  }}
                >
                  {portalError}
                </p>
              )}
            </section>

            {/* Pack purchase in progress */}
            {purchaseState && (
              <PayPackPanel
                offeringId={purchaseState.offeringId}
                amountMinor={purchaseState.amountMinor}
                currency={purchaseState.currency}
                publishableKey={getStripePublishableKey()}
                stripeAccountId={purchaseState.stripeAccountId}
                onPaid={handlePaid}
                onError={(msg) => setError(msg)}
                onCancel={() => setPurchaseState(null)}
              />
            )}

            {/* Buy a pack */}
            {activeOfferings.length > 0 && (
              <section style={{ marginBottom: '2rem' }}>
                <SectionTitle>Buy a pack</SectionTitle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '0.75rem',
                  }}
                >
                  {activeOfferings.map((offering: PackOffering) => (
                    <PackOfferingCard
                      key={offering.id}
                      offering={offering}
                      onBuy={() => startPackPurchase(offering)}
                      disabled={!!purchaseState}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Subscribe to a membership */}
            {activeTiers.length > 0 && (
              <section style={{ marginBottom: '2rem' }}>
                <SectionTitle>Subscribe to a membership</SectionTitle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '0.75rem',
                  }}
                >
                  {activeTiers.map((tier: MembershipTier) => (
                    <MembershipTierCard
                      key={tier.id}
                      tier={tier}
                      onSubscribe={() => startSubscribe(tier.id)}
                      disabled={!!subscribingTierId}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
